/**
 * 帳票一括作成ジョブ API（/api/report-jobs。担当: G4）
 * G2・G3 の一覧画面（定時連絡・速報・別記3・別記4・台紙）からは `useRequestReportJob()` か
 * `<BulkReportButton>`（./BulkReportButton）で一括作成を依頼する。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type BulkReportId = 'R-03' | 'R-04' | 'R-09' | 'R-10' | 'R-11';
export type ReportJobStatus = '待機' | '処理中' | '完了' | '失敗';

export interface ReportJobDto {
  id: number;
  reportId: string;
  reportName: string;
  fiscalYear: number | null;
  businessOperatorId: number | null;
  params: Record<string, unknown>;
  status: ReportJobStatus;
  totalCount: number | null;
  processedCount: number;
  errorMessage: string | null;
  fileId: number | null;
  fileName: string | null;
  createdBy: number | null;
  createdByName: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

export const reportJobKeys = {
  all: ['report-jobs'] as const,
  list: (fiscalYear?: number) => [...reportJobKeys.all, 'list', fiscalYear ?? 'all'] as const,
};

export function useReportJobs(fiscalYear?: number) {
  return useQuery({
    queryKey: reportJobKeys.list(fiscalYear),
    queryFn: ({ signal }) => api.get<{ items: ReportJobDto[] }>('/report-jobs', { fiscalYear }, { signal }),
    // 待機・処理中のジョブがある間は 3 秒ごとに更新
    refetchInterval: (q) => (q.state.data?.items.some((j) => j.status === '待機' || j.status === '処理中') ? 3000 : false),
  });
}

export interface RequestReportJobInput {
  reportId: BulkReportId;
  fiscalYear: number;
  /** 一覧 API と同じ検索条件 */
  params: Record<string, unknown>;
}

export function useRequestReportJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RequestReportJobInput) => api.post<{ id: number }>('/report-jobs', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: reportJobKeys.all }),
  });
}
